import { countByType } from "./board.js";
export function newDrawState() {
    return { positions: new Map(), kingOnlyPlies: 0, endgame: null };
}
/**
 * Classify the material for the FMJD endgame rules.
 *
 * 6.3 applies when one side has exactly one king (and nothing else) and the
 * other has exactly three pieces including at least one king.
 * 6.4 applies when one side has exactly one king and the other has at most
 * two pieces including at least one king.
 */
export function classifyEndgame(v, board) {
    const rules = v.drawRules;
    if (rules.endgame3v1Moves === null && rules.endgame2v1Moves === null)
        return null;
    const w = countByType(board, "w");
    const b = countByType(board, "b");
    for (const [strong, weak, strongSide] of [
        [w, b, "w"],
        [b, w, "b"],
    ]) {
        // The weak side must be a lone king.
        if (weak.total !== 1 || weak.kings !== 1)
            continue;
        // The strong side must include at least one king, else neither rule applies.
        if (strong.kings < 1)
            continue;
        if (strong.total === 3 && rules.endgame3v1Moves !== null) {
            return { rule: "3v1", strongSide };
        }
        if (strong.total <= 2 && rules.endgame2v1Moves !== null) {
            return { rule: "2v1", strongSide };
        }
    }
    return null;
}
/**
 * Advance the draw counters by one ply.
 *
 * `wasManMove` and `wasCapture` describe the ply just played; `board` is the
 * resulting position.
 */
export function advance(v, state, board, wasManMove, wasCapture) {
    // 6.2 — only kings moved, no man move, no capture.
    if (wasManMove || wasCapture)
        state.kingOnlyPlies = 0;
    else
        state.kingOnlyPlies++;
    // 6.3 / 6.4 — endgame counters.
    const classified = classifyEndgame(v, board);
    if (!classified) {
        state.endgame = null;
        return;
    }
    const limitMoves = classified.rule === "3v1" ? v.drawRules.endgame3v1Moves : v.drawRules.endgame2v1Moves;
    if (limitMoves === null) {
        state.endgame = null;
        return;
    }
    const active = state.endgame;
    if (active && active.rule === classified.rule && active.strongSide === classified.strongSide) {
        // Same configuration: keep counting. Note that a capture does NOT reset
        // this counter (6.3, second sentence).
        active.plies++;
    }
    else {
        // Material has just entered this configuration — start a fresh count.
        state.endgame = {
            rule: classified.rule,
            strongSide: classified.strongSide,
            plies: 1,
            limitPlies: limitMoves * 2,
        };
    }
}
/**
 * Is the position drawn by rule? Returns the reason, or null.
 *
 * `positionKey` is the key of the current position with the side to move,
 * already counted into `state.positions` by the caller.
 *
 * Note the caller must check for a win (no pieces / no legal moves) *before*
 * calling this, which is what implements the 6.3 exception: a position that is
 * lost under 7.2.2/7.2.3 takes precedence over the 16-move draw.
 */
export function drawReason(v, state, positionKey) {
    if ((state.positions.get(positionKey) ?? 0) >= v.drawRules.repetition) {
        return "repetition"; // 6.1
    }
    if (state.kingOnlyPlies >= v.drawRules.noProgressPlies) {
        return "no-progress"; // 6.2
    }
    const eg = state.endgame;
    if (eg && eg.plies >= eg.limitPlies) {
        return eg.rule === "3v1" ? "endgame-3v1" : "endgame-2v1"; // 6.3 / 6.4
    }
    return null;
}
